import { useRef } from 'react';
import { useEffectOnce } from '@core/logic';

export const useUserActivity = ({
  secondsToUserInactive,
  onUserActivePulse,
  onUnmount,
  onTabVisibilityChange,
}: {
  secondsToUserInactive: number;
  onUserActivePulse: (hasUserBeenActive: boolean) => void;
  onUnmount: (hasUserBeenActive: boolean) => void;
  onTabVisibilityChange: (hasUserBeenActive: boolean, isTabVisible: boolean) => void;
}): void => {
  const timeoutTillUserInactive = useRef<number | undefined>(undefined);
  const pulse = useRef<number | undefined>(undefined);

  const clearUserActivePulse = () => {
    window.clearTimeout(pulse.current);
    pulse.current = undefined;
  };

  const clearTimeoutTillUserInactive = () => {
    window.clearTimeout(timeoutTillUserInactive.current);
    timeoutTillUserInactive.current = undefined;
  };

  const clearAllTimeouts = () => {
    onUserActivePulse(false);
    clearUserActivePulse();
    clearTimeoutTillUserInactive();
  };

  const resetTimeoutTillUserInactive = () => {
    clearTimeoutTillUserInactive(); // this is important to stop the timeout from running multiple times
    timeoutTillUserInactive.current = window.setTimeout(
      clearAllTimeouts,
      secondsToUserInactive * 1000,
    );
  };

  const startUserActivePulse = () => {
    onUserActivePulse(!!pulse.current);
    pulse.current = window.setTimeout(startUserActivePulse, secondsToUserInactive * 1000);
  };

  const handleUserActivity = () => {
    if (!pulse.current) startUserActivePulse();
    resetTimeoutTillUserInactive();
  };

  const handleVisibilityChange = () => {
    onTabVisibilityChange(!!pulse.current, !document.hidden);

    if (document.hidden) {
      clearAllTimeouts();
    } else {
      resetTimeoutTillUserInactive();
    }
  };

  useEffectOnce(() => {
    window.addEventListener('click', handleUserActivity);
    window.addEventListener('mousemove', handleUserActivity);
    window.addEventListener('keypress', handleUserActivity);
    window.addEventListener('visibilitychange', handleVisibilityChange);
    return () => {
      onUnmount(!!pulse.current); // attention: order here is important since pulse is being reset by clearAllTimeouts()
      clearAllTimeouts();
      window.removeEventListener('click', handleUserActivity);
      window.removeEventListener('mousemove', handleUserActivity);
      window.removeEventListener('keypress', handleUserActivity);
      window.removeEventListener('visibilitychange', handleVisibilityChange);
    };
  });
};
